import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';

/**
 * Extension du type axios pour supporter le flag opt-out des toasts.
 * Un hook qui gère lui-même l'affichage de l'erreur (dialog, alert-strip)
 * peut passer `{ _silentError: true }` dans la config pour éviter la
 * double notification.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    _silentError?: boolean;
  }
}

/**
 * Détermine dynamiquement l'URL de l'API selon le hostname (CTO-ARCH-001) :
 * - app.simgroupci.com → api.simgroupci.com/v1 (PROD)
 * - staging-app.simgroupci.com → staging.simgroupci.com/v1 (STAGING)
 * - localhost, IPs, ngrok, etc. → fallback configurable via VITE_API_URL
 * - Sans override : staging (filet de sécurité pour dev local)
 */
function resolveApiUrl(): string {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.simgroupci.com')         return 'https://api.simgroupci.com/v1';
    if (host === 'staging-app.simgroupci.com') return 'https://staging.simgroupci.com/v1';
  }
  return 'https://staging.simgroupci.com/v1';
}

const BASE_URL = resolveApiUrl();

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Inject JWT + header PDG View automatiquement sur toutes les requêtes ───
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const state = useAuthStore.getState();
  if (state.accessToken) {
    config.headers.Authorization = `Bearer ${state.accessToken}`;
  }
  // CTO-ACCESS-001 — Mode PDG View Filiale (admin_holding — lecture seule par défaut)
  if (state.pdgViewFiliale) {
    config.headers['X-PDG-View-Filiale'] = state.pdgViewFiliale;
    // Toggle écriture (ticket 14/09/2026) — n'envoyer le header que si activé
    if (state.pdgViewWrite) {
      config.headers['X-PDG-View-Write'] = 'true';
    }
  }
  return config;
});

// ── Refresh automatique sur 401 (sauf endpoints d'auth eux-mêmes) ───────────
let refreshPromise: Promise<boolean> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (
      error.response?.status === 401 &&
      !original?._retry &&
      original?.url &&
      !original.url.includes('/auth/login') &&
      !original.url.includes('/auth/refresh') &&
      !original.url.includes('/auth/login/2fa')
    ) {
      original._retry = true;
      // Un seul rafraîchissement à la fois, même si plusieurs requêtes tombent
      // en 401 ensemble. Le remise à zéro appartient à la promesse elle-même :
      // quand chaque appelant la faisait après son propre await, une requête
      // arrivant juste après effaçait le partage et lançait un second
      // rafraîchissement. Or Supabase ne sert qu'une fois un jeton de
      // renouvellement — le second échouait et déconnectait l'utilisateur.
      if (!refreshPromise) {
        refreshPromise = useAuthStore
          .getState()
          .refresh()
          .finally(() => { refreshPromise = null; });
      }
      const refreshed = await refreshPromise;
      if (refreshed) {
        const token = useAuthStore.getState().accessToken;
        if (token && original.headers) {
          original.headers.Authorization = `Bearer ${token}`;
        }
        return apiClient(original);
      }
      useAuthStore.getState().logout();
    }

    // Toast global des erreurs API (ticket SIMGROUP-2026-002 M-01)
    // Avant : refus silencieux, l'utilisateur cliquait dans le vide.
    // Maintenant : chaque erreur produit une notification visible et persistante
    // (jusqu'à fermeture par l'user pour les erreurs).
    //
    // Skip :
    //   - _silentError=true (le hook gère lui-même l'affichage dans dialog/strip)
    //   - 401 (déjà géré via refresh ou logout ci-dessus)
    //   - routes d'auth (feedback géré dans les pages Login/2FA)
    const status = error.response?.status;
    const url    = original?.url ?? '';
    const isAuthRoute = url.includes('/auth/');
    const isSilent    = original?._silentError === true;
    if (!isSilent && !isAuthRoute && status !== 401) {
      const err = extractError(error);
      const shortUrl = url.replace(/^https?:\/\/[^/]+/, '');
      const method   = (original?.method ?? '').toUpperCase();
      toast.error(
        `${err.code} · ${status ?? 'Réseau'}`,
        `${err.message}\n${method} ${shortUrl}`,
      );
    }

    return Promise.reject(error);
  },
);

/** Helper : extrait le payload `data` d'une réponse API success */
export function unwrap<T>(response: { data: { data: T } }): T {
  return response.data.data;
}

/**
 * Helper pour les endpoints paginés. Le TransformInterceptor backend
 * met `data` et `meta` au TOP-level (pas imbriqué dans `data:{data,meta}`).
 * Reconstruit la structure { data, meta } attendue côté front.
 */
export function unwrapPaginated<T>(response: {
  data: { data: T[]; meta: { page: number; limit: number; total: number; pages: number } };
}): { data: T[]; meta: { page: number; limit: number; total: number; pages: number } } {
  return { data: response.data.data, meta: response.data.meta };
}

/**
 * Extrait le message d'erreur API, et distingue les pannes entre elles.
 *
 * Avant le 29/09/2026, tout ce qui n'avait pas l'enveloppe `{ error }` du
 * backend devenait « NETWORK_ERROR · Impossible de joindre le serveur ». Le PDG
 * a donc lu ce message pendant un redéploiement de l'API : le serveur était
 * joignable, il redémarrait. Un diagnostic faux envoie chercher au mauvais
 * endroit — ici, du côté de sa connexion plutôt que du nôtre.
 */
export function extractError(err: unknown): { code: string; message: string } {
  if (axios.isAxiosError(err) && err.response?.data?.error) {
    return err.response.data.error;
  }

  if (axios.isAxiosError(err)) {
    const statut = err.response?.status;

    // 502/503/504 : le serveur est là mais ne répond pas — typiquement un
    // redéploiement en cours. Dire « réessayez » plutôt que de laisser croire
    // à un problème de réseau chez l'utilisateur.
    if (statut && statut >= 500) {
      return {
        code: 'SERVEUR_INDISPONIBLE',
        message: statut === 502 || statut === 503 || statut === 504
          ? 'Le serveur redémarre ou est momentanément indisponible. Réessayez dans un instant.'
          : `Erreur serveur (${statut}). Si cela se répète, signalez-le.`,
      };
    }

    if (err.code === 'ECONNABORTED') {
      // 30/10/2026 : un encaissement a été enregistré APRÈS le délai, et « Réessayez » invitait à le saisir deux fois.
      // Une écriture (tout sauf une lecture) a pu aboutir : on le dit, au lieu de pousser à recommencer.
      const lecture = (err.config?.method ?? 'get').toLowerCase() === 'get';
      return {
        code: 'DELAI_DEPASSE',
        message: lecture
          ? 'Le serveur met trop de temps à répondre. Réessayez.'
          : 'Le serveur a mis trop de temps à répondre : la demande a peut-être été enregistrée. Vérifiez avant de recommencer.',
      };
    }

    // Aucune réponse du tout : soit l'appareil est hors ligne, soit le serveur
    // refuse la connexion. Le navigateur sait lequel, autant le dire.
    if (!err.response) {
      return typeof navigator !== 'undefined' && navigator.onLine === false
        ? { code: 'HORS_LIGNE', message: 'Vous semblez hors ligne. Vérifiez votre connexion.' }
        : { code: 'SERVEUR_INJOIGNABLE', message: 'Le serveur ne répond pas. Il redémarre peut-être — réessayez dans un instant.' };
    }
  }

  return { code: 'ERREUR_INATTENDUE', message: 'Une erreur inattendue est survenue.' };
}
